import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { analytics } from '@heycatch/sdk';
import { api, setToken, getToken } from '@/lib/api';

const AuthCtx = createContext(null);

const identify = (u) => {
  if (!u?.id) return;
  analytics.setIdentity(
    u.id,
    { email: u.email, name: u.display_name },
    { signup_date: u.created_at },
  );
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
      identify(r.data);
      return r.data;
    } catch (e) {
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    identify(r.data.user);
    return r.data.user;
  };

  const signup = async (email, password, display_name) => {
    const r = await api.post('/auth/signup', { email, password, display_name });
    setToken(r.data.token);
    setUser(r.data.user);
    identify(r.data.user);
    analytics.trackEvent('signup_completed');
    return r.data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    analytics.resetIdentity();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
